import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type {
  ExternalAssetListGroupDto,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import { EXTERNAL_NETWORK_ASSET_KINDS } from '../../../../hooks/api/useSurfaceAssets';
import { useListPageGroupCollapse } from '../../../common/useListPageGroupCollapse';
import { buildExternalAssetListCells } from '../build-external-asset-list-cells';
import { getExternalAssetListLabel } from '../externalAssetDisplayLabels';
import { readExternalAssetDetectionMetrics } from '../read-external-asset-row-fields';
import {
  EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP,
  getExternalNetworkEntityKindLabel,
} from '../surfaceAsset.constants';
import { useAssetListDisplay } from '../useAssetListDisplay';
import {
  EXTERNAL_ASSETS_EMPTY_GROUP_KEY,
  buildExternalAssetsTableRows,
  isExternalAssetsGroupHeaderRow,
  resolveExternalAssetsLoadMoreRowIndex,
  type ExternalAssetGridItem,
  type ExternalAssetsGroupByField,
  type ExternalAssetsGrouping,
} from './external-assets-table.model';
import type { ExternalAssetEntry } from './useExternalAssetsListData';

export function useExternalAssetsTableRows({
  entries,
  selectedKind,
  groupBy,
  groups,
  kindTotals,
  resetKey,
}: {
  entries: readonly ExternalAssetEntry[];
  selectedKind: ExternalNetworkEntityKind | null;
  groupBy: ExternalAssetsGroupByField | undefined;
  groups: readonly ExternalAssetListGroupDto[];
  kindTotals: ReadonlyMap<string, number>;
  resetKey: string;
}) {
  const tEntityKinds = useTranslations('externalNetwork.entityKinds');
  const tTable = useTranslations('proxy.assets.table');
  const display = useAssetListDisplay();
  const {
    collapsedGroupKeys,
    toggleGroup,
    expandAllGroups,
    collapseGroups,
    areAllCollapsed,
  } = useListPageGroupCollapse({ resetKey });

  const showType = selectedKind === null;
  const items = useMemo(
    () =>
      entries.map(({ entityKind, asset }): ExternalAssetGridItem => {
        const raw: Record<string, unknown> = Object.fromEntries(
          Object.entries(asset),
        );
        const id = String(raw.id ?? '');

        return {
          id,
          rowId: `${entityKind}-${id}`,
          entityKind,
          label: getExternalAssetListLabel({ entityKind, asset: raw }),
          metrics: readExternalAssetDetectionMetrics({ raw }),
          cells: buildExternalAssetListCells({
            entityKind,
            raw,
            typeLabel: showType
              ? getExternalNetworkEntityKindLabel({
                  kind: entityKind,
                  t: tEntityKinds,
                })
              : undefined,
            typeColorPalette: showType
              ? EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP[entityKind]
              : undefined,
            ...display,
          }),
        };
      }),
    [display, entries, showType, tEntityKinds],
  );

  const grouping = useMemo((): ExternalAssetsGrouping => {
    if (selectedKind === null) {
      return {
        mode: 'kind',
        kinds: EXTERNAL_NETWORK_ASSET_KINDS,
        totals: kindTotals,
      };
    }

    return groupBy ? { mode: 'field', groups } : { mode: 'none' };
  }, [groupBy, groups, kindTotals, selectedKind]);

  const getGroupLabel = useCallback(
    (groupKey: string): string => {
      if (grouping.mode === 'kind') {
        const kind = EXTERNAL_NETWORK_ASSET_KINDS.find(
          (candidate) => candidate === groupKey,
        );

        return kind
          ? getExternalNetworkEntityKindLabel({ kind, t: tEntityKinds })
          : groupKey;
      }

      if (groupKey === EXTERNAL_ASSETS_EMPTY_GROUP_KEY) {
        return tTable('emptyGroup');
      }

      if (groupBy === 'isTopLevel') {
        return groupKey === 'true'
          ? display.topLevelLabel
          : display.subdomainLevelLabel;
      }

      if (groupBy === 'scope') {
        return (
          display.scopeCells({ scope: groupKey }).scopeLabel ??
          display.formatSource({ sourceType: groupKey }) ??
          groupKey
        );
      }

      return display.formatSource({ sourceType: groupKey }) ?? groupKey;
    },
    [display, groupBy, grouping.mode, tEntityKinds, tTable],
  );

  const rows = useMemo(
    () =>
      buildExternalAssetsTableRows({
        items,
        grouping,
        collapsedGroupKeys,
        getGroupLabel,
      }),
    [collapsedGroupKeys, getGroupLabel, grouping, items],
  );
  const loadMoreRowIndex = resolveExternalAssetsLoadMoreRowIndex({
    rows,
    grouping,
    collapsedGroupKeys,
  });
  const groupKeys = useMemo(
    () =>
      rows.flatMap((row) =>
        isExternalAssetsGroupHeaderRow(row) ? [row.groupKey] : [],
      ),
    [rows],
  );

  return {
    rows,
    loadMoreRowIndex,
    collapsedGroupKeys,
    areAllGroupsCollapsed: areAllCollapsed(groupKeys),
    toggleGroup,
    expandAllGroups,
    collapseAllGroups: () => collapseGroups(groupKeys),
  };
}
